import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { useAccessContext, useOrg } from '@/providers';
import { Button } from '@/components/ui/button';
import { useGetStaffMe } from '@/api/generated/staff/staff';
import { UserSettingsSkeleton } from './user-settings/user-settings-skeleton';
import { UserSettingsView } from './user-settings/user-settings-view';
import { formatDocumentTitle } from '@/lib/document-title';

export const Route = createFileRoute('/_auth/$orgId/user-settings')({
  component: UserSettingsPage,
});

function UserSettingsPage() {
  useOrg(); // Ensure we're in org context
  const access = useAccessContext();
  // Agency members have no staff row in a salon; their profile lives in the
  // agency workspace (old links/bookmarks land here).
  if (access.actorType === 'AGENCY_MEMBER') {
    return <Navigate to="/agency/profile" replace />;
  }
  return <StaffUserSettingsPage />;
}

function StaffUserSettingsPage() {
  // Profile data is stable: skeleton on first load only, background refreshes stay silent.
  const { data: meResponse, isPending, isFetching, refetch } = useGetStaffMe();
  const me = meResponse?.data;

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('User Settings')}</title>
      </Helmet>

      <div className="h-full min-h-0 overflow-y-auto bg-clovie-canvas">
        <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 lg:py-10">
          {isPending ? (
            <UserSettingsSkeleton />
          ) : !me ? (
            <div
              role="alert"
              className="max-w-xl rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-6 text-clovie-text shadow-soft"
            >
              <p className="text-base text-clovie-text">Couldn&apos;t load your settings.</p>
              <Button variant="outline" className="mt-4 min-h-11" loading={isFetching} onClick={() => void refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <UserSettingsView me={me} />
          )}
        </div>
      </div>
    </>
  );
}
